import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, Badge } from "../components/Shared";
import { toast } from "sonner";
import { ClockCounterClockwise, MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";

export default function LegacyCohort() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pipeline, setPipeline] = useState("all");
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (q.trim()) params.set("q", q.trim());
      if (pipeline !== "all") params.set("pipeline", pipeline);
      const { data: res } = await api.get(`/aspirants/legacy/candidates?${params.toString()}`);
      setData(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar");
    } finally { setLoading(false); }
  }, [q, pipeline]);

  useEffect(() => { load(); }, [load]);

  const items = data.items || [];
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allChecked = items.length > 0 && items.every((u) => selected[u.id]);

  const toggleAll = () => {
    if (allChecked) { setSelected({}); return; }
    const next = {};
    items.forEach((u) => { next[u.id] = true; });
    setSelected(next);
  };

  const reactivate = async () => {
    if (selectedIds.length === 0) { toast.error("Selecciona al menos un lead"); return; }
    if (!window.confirm(`Reactivar ${selectedIds.length} lead(s) en el Journey V2? Se preserva su historial.`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/aspirants/reactivate-v2-bulk`, { user_ids: selectedIds });
      toast.success(`Reactivados: ${res.reactivated} · Ya en V2: ${res.already_v2}`);
      setSelected({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reactivar");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="legacy-cohort-page">
      <div className="flex items-center gap-3 mb-1">
        <ClockCounterClockwise size={22} className="text-terracotta" />
        <h1 className="font-heading text-2xl">Cohorte Legacy</h1>
        <Badge tone="neutral">{data.total} pendiente(s)</Badge>
      </div>
      <p className="text-sm text-carbon-500 mb-5 max-w-2xl">
        Leads del journey anterior que aún no están en V2. Reactívalos en lote: se preserva su historial y sus
        estados viejos se guardan como contexto (nunca como evidencia del nuevo journey).
      </p>

      <Card className="p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex items-center gap-2 flex-1 bg-page border border-carbon-200 rounded px-3">
            <MagnifyingGlass size={16} className="text-carbon-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Buscar por nombre, ID o WhatsApp…" data-testid="legacy-search"
              className="flex-1 bg-transparent py-2 text-sm outline-none"
            />
          </div>
          <select value={pipeline} onChange={(e) => setPipeline(e.target.value)}
            data-testid="legacy-pipeline-filter"
            className="bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
            <option value="all">Todos los pipelines</option>
            <option value="founder">Founder</option>
            <option value="ambassador">Ambassador</option>
          </select>
          <button onClick={reactivate} disabled={busy || selectedIds.length === 0}
            data-testid="bulk-reactivate-btn"
            className="bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-50 whitespace-nowrap">
            {busy ? "Reactivando…" : `Reactivar a V2 (${selectedIds.length})`}
          </button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-page border-b border-carbon-200">
            <tr className="text-left text-carbon-500">
              <th className="p-3 w-10">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="legacy-select-all" />
              </th>
              <th className="p-3">Lead</th>
              <th className="p-3">WhatsApp</th>
              <th className="p-3">Pipeline</th>
              <th className="p-3">Estados previos</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-carbon-400">Cargando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-carbon-400">No hay leads legacy pendientes. 🎉</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.id} className="border-b border-carbon-100 hover:bg-page" data-testid={`legacy-row-${u.id}`}>
                <td className="p-3">
                  <input type="checkbox" checked={!!selected[u.id]}
                    onChange={() => setSelected({ ...selected, [u.id]: !selected[u.id] })}
                    data-testid={`legacy-check-${u.id}`} />
                </td>
                <td className="p-3">
                  <div className="font-medium text-carbon-900">{u.name || "—"}</div>
                  <div className="text-[11px] text-carbon-400">{u.public_id}</div>
                </td>
                <td className="p-3 text-carbon-600">{u.whatsapp || "—"}</td>
                <td className="p-3"><Badge tone="neutral">{u.pipeline_slug || "—"}</Badge></td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.states || []).slice(0, 3).map((s, i) => <Badge key={i}>{s}</Badge>)}
                    {(u.states || []).length === 0 && <span className="text-carbon-400 text-xs">—</span>}
                  </div>
                </td>
                <td className="p-3 text-right">
                  <Link to={`/users/${u.id}`} className="inline-flex items-center gap-1 text-terracotta text-xs hover:underline">
                    Ver <ArrowRight size={12} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
